/**
 * PD-ETA-01：兩點間後端估時。失敗顯示「暫時無法預估」，不阻擋行程。
 * UNAVAILABLE／未啟用時不假裝有數字。
 */
import { useState } from 'react';
import { journeyRoutingApi } from '../../api/journeyRoutingApi';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';

function formatEstimate(result) {
  if (!result || result.status !== 'OK') return null;
  const meters = Number(result.distanceMeters);
  const seconds = Number(result.durationSeconds);
  if (!Number.isFinite(meters) || !Number.isFinite(seconds)) return null;
  const km = meters >= 1000 ? `${(meters / 1000).toFixed(1)} 公里` : `${Math.round(meters)} 公尺`;
  const minutes = Math.max(1, Math.round(seconds / 60));
  return `約 ${km}、${minutes} 分鐘（預估）`;
}

export default function ActivityRoutingEstimate({
  journeyId,
  fromLocationId,
  toLocationId,
  transportMode = null,
}) {
  const [phase, setPhase] = useState('idle');
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');

  if (!journeyId || !fromLocationId || !toLocationId) return null;

  async function onEstimate() {
    setPhase('loading');
    setError('');
    setLabel('');
    try {
      const result = await journeyRoutingApi.estimate(journeyId, {
        fromLocationId,
        toLocationId,
        transportMode: transportMode || null,
      });
      if (result?.status === 'OK') {
        const text = formatEstimate(result);
        setLabel(text || '暫時無法預估');
        setPhase(text ? 'ready' : 'unavailable');
      } else {
        setLabel('暫時無法預估');
        setPhase('unavailable');
      }
    } catch (cause) {
      setError(resolveSafeUserVisibleError(
        cause,
        () => '暫時無法預估',
        'travel.common.actionFailed',
      ));
      setPhase('error');
    }
  }

  return (
    <div className="journey-planning__routing-estimate" data-testid="routing-estimate">
      <p className="journey-planning__routing-estimate-hint" data-testid="routing-estimate-idle">
        {transportMode
          ? `交通：${transportMode}`
          : '尚未選擇交通方式 · 距離與時間待計算'}
      </p>
      <button
        type="button"
        className="journey-planning__routing-estimate-btn"
        data-testid="routing-estimate-button"
        onClick={onEstimate}
        disabled={phase === 'loading'}
      >
        {phase === 'loading' ? '預估中…' : '預估路程'}
      </button>
      {label ? <span data-testid="routing-estimate-label">{label}</span> : null}
      {error ? <span role="alert">{error}</span> : null}
    </div>
  );
}

export function activityLocationId(activity) {
  const id = activity?.location?.id ?? activity?.locationId ?? null;
  if (id == null || id === '') return null;
  const text = String(id);
  return /^[1-9]\d*$/.test(text) ? text : null;
}
